/** Parffragrance quiz v5: customer-facing questions and matching configuration. */
(() => {
  'use strict';

  const PRIVATE_COLLECTION_HANDLE = 'private-collection';
  const LUXURY_COLLECTION_HANDLE = 'luxuryy-perfumes';
  const PAGE_SIZE = 250;
  const MAX_CATALOG_PAGES = 12;
  const INTENSE_TERMS = /\b(intense|intens|elixir|extrait|absolu|absolute|extreme|extr[eê]me|forte|concentrated)\b/i;
  const NON_PERFUME_TERMS = /\b(body\s*(mist|spray|scent)|home\s*(scent|spray|fragrance)|room\s*spray|air\s*freshener|diffuser|candle|linen\s*spray|fabric\s*spray|car\s*freshener|deodorant)\b/i;

  const FAMILY_ALIASES = {
    citrus: ['citrus', 'bergamot', 'lemon', 'lime', 'orange', 'mandarin', 'grapefruit', 'neroli'],
    fresh: ['fresh', 'aquatic', 'marine', 'sea', 'water', 'ozonic', 'green', 'mint'],
    floral: ['floral', 'flower', 'rose', 'jasmine', 'tuberose', 'iris', 'violet', 'ylang', 'peony', 'orange blossom', 'lavender'],
    fruity: ['fruity', 'fruit', 'berry', 'berries', 'pear', 'apple', 'peach', 'plum', 'cherry', 'lychee', 'litchi', 'mango', 'pineapple', 'fig', 'blackcurrant', 'black currant', 'raspberry', 'strawberry'],
    woody: ['woody', 'wood', 'cedar', 'sandalwood', 'vetiver', 'patchouli', 'cashmeran', 'oakmoss'],
    amber: ['amber', 'oriental', 'labdanum', 'benzoin', 'resin', 'incense'],
    spicy: ['spicy', 'spice', 'saffron', 'pepper', 'cardamom', 'cinnamon', 'nutmeg', 'clove', 'ginger'],
    oud: ['oud', 'agarwood'],
    leather: ['leather', 'suede', 'tobacco', 'smoky', 'smoke'],
    sweet: ['sweet', 'vanilla', 'caramel', 'praline', 'honey', 'sugar', 'chocolate', 'cocoa', 'gourmand', 'tonka'],
    musk: ['musk', 'musky', 'white musk', 'skin musk', 'clean musk'],
    powdery: ['powdery', 'powder', 'orris', 'iris', 'violet', 'heliotrope', 'cosmetic', 'talc']
  };

  const QUESTIONS = [
    {
      id: 'gender',
      title: 'Who are you shopping for?',
      help: 'Choose the closest option. Unisex fragrances may also appear when they suit your preferences.',
      options: [
        { label: 'For him', icon: '♂', description: 'Men’s and suitable unisex fragrances', value: 'men' },
        { label: 'For her', icon: '♀', description: 'Women’s and suitable unisex fragrances', value: 'women' },
        { label: 'Unisex', icon: '◇', description: 'Only fragrances designed for everyone', value: 'unisex' },
        { label: 'No preference', icon: '∞', description: 'Show the best matches across all options', value: 'any' }
      ]
    },
    {
      id: 'intensity',
      title: 'How strong would you like it?',
      help: 'We will match the exact intensity you choose.',
      options: [
        { label: 'Regular', icon: '◌', description: 'Balanced and easy to wear throughout the day', value: 'regular' },
        { label: 'Intense', icon: '◆', description: 'Richer concentration with stronger projection', value: 'intense' }
      ]
    },
    {
      id: 'occasion',
      title: 'When will you wear it most?',
      help: 'Choose the occasion that best fits your routine.',
      options: [
        { label: 'Every day', icon: '☀', description: 'Easy, versatile and approachable', value: 'daily', weights: { fresh: 3, citrus: 2, musk: 1 } },
        { label: 'Work & meetings', icon: '▣', description: 'Polished, confident and not overpowering', value: 'office', weights: { woody: 3, fresh: 2, musk: 2 } },
        { label: 'Dates & evenings', icon: '♡', description: 'Warm, inviting and memorable', value: 'date', weights: { sweet: 3, amber: 3, floral: 2, musk: 1 } },
        { label: 'Nightlife & occasions', icon: '✦', description: 'Bold, expressive and attention-grabbing', value: 'night', weights: { amber: 3, spicy: 3, leather: 2, oud: 2 } },
        { label: 'Weddings & events', icon: '♢', description: 'Elegant, refined and distinctive', value: 'special', weights: { floral: 3, woody: 2, amber: 2, spicy: 1 } }
      ]
    },
    {
      id: 'climate',
      title: 'Which weather will you wear it in?',
      help: 'Think about where you will use the fragrance most often.',
      options: [
        { label: 'Hot weather', icon: '☼', description: 'Fresh, citrus, aquatic and clean profiles', value: 'hot', weights: { fresh: 4, citrus: 4, musk: 1 } },
        { label: 'Mostly indoors', icon: '◐', description: 'Balanced scents for air-conditioned settings', value: 'mild', weights: { woody: 2, floral: 2, fresh: 2, musk: 1 } },
        { label: 'Cool evenings', icon: '❄', description: 'Warm, spicy, sweet, oud and leather profiles', value: 'cool', weights: { amber: 4, spicy: 3, sweet: 3, oud: 2, leather: 2 } },
        { label: 'All year round', icon: '◎', description: 'Versatile fragrances for different settings', value: 'all', weights: { woody: 2, fresh: 2, musk: 1 } }
      ]
    },
    {
      id: 'mood',
      title: 'How should your fragrance feel?',
      help: 'Choose the impression you would like it to create.',
      options: [
        { label: 'Fresh & energising', icon: '↗', description: 'Bright, crisp and refreshing', value: 'clean', weights: { fresh: 4, citrus: 3, musk: 2 } },
        { label: 'Elegant & refined', icon: '◇', description: 'Smooth, polished and sophisticated', value: 'elegant', weights: { woody: 3, floral: 3, musk: 1 } },
        { label: 'Warm & comforting', icon: '⌁', description: 'Cosy, sweet and inviting', value: 'warm', weights: { sweet: 4, amber: 3, spicy: 1 } },
        { label: 'Bold & mysterious', icon: '◼', description: 'Deep, smoky and commanding', value: 'bold', weights: { oud: 4, leather: 3, amber: 3, spicy: 2 } },
        { label: 'Sensual & romantic', icon: '♥', description: 'Soft, alluring and intimate', value: 'sensual', weights: { floral: 3, sweet: 3, musk: 3, amber: 1 } }
      ]
    },
    {
      id: 'family',
      title: 'Which scent styles do you enjoy most?',
      help: 'Choose up to 3. We’ll use every selected style as a strong matching signal.',
      maxSelections: 3,
      options: [
        { label: 'Sweet', icon: '●', description: 'Vanilla, praline, caramel, tonka and gourmand notes', value: 'sweet', weights: { sweet: 8 } },
        { label: 'Musky', icon: '○', description: 'Musk, skin-like and soft clean notes', value: 'musk', weights: { musk: 8 } },
        { label: 'Powdery', icon: '◌', description: 'Iris, orris, violet, heliotrope and soft powder notes', value: 'powdery', weights: { powdery: 8 } },
        { label: 'Oud', icon: '◆', description: 'Oud and agarwood-led fragrances', value: 'oud', weights: { oud: 8 } },
        { label: 'Leather', icon: '■', description: 'Leather, suede, tobacco and smoky notes', value: 'leather', weights: { leather: 8 } },
        { label: 'Floral', icon: '✿', description: 'Rose, jasmine, tuberose and white florals', value: 'floral', weights: { floral: 8 } },
        { label: 'Fruity', icon: '◉', description: 'Berries, pear, peach, cherry, lychee and tropical fruits', value: 'fruity', weights: { fruity: 8 } },
        { label: 'Spicy', icon: '✦', description: 'Saffron, pepper, cardamom, cinnamon and warm spices', value: 'spicy', weights: { spicy: 8 } },
        { label: 'Amber', icon: '✧', description: 'Amber, benzoin, labdanum, resin and incense', value: 'amber', weights: { amber: 8 } },
        { label: 'Woody', icon: '▲', description: 'Cedar, sandalwood, vetiver, patchouli and woods', value: 'woody', weights: { woody: 8 } }
      ]
    },
    {
      id: 'avoid',
      title: 'Anything you would rather avoid?',
      help: 'Choose one option, or keep everything open.',
      options: [
        { label: 'No exclusions', icon: '✓', description: 'Keep every scent family in your results', value: 'none' },
        { label: 'Very sweet scents', icon: '−', description: 'Avoid sugary, vanilla-heavy and dessert-like profiles', value: 'sweet' },
        { label: 'Oud & leather', icon: '−', description: 'Avoid deep agarwood, leather and smoky profiles', value: 'oud-leather' },
        { label: 'Strong florals', icon: '−', description: 'Avoid fragrances led by prominent floral notes', value: 'floral' },
        { label: 'Citrus & aquatic', icon: '−', description: 'Avoid bright citrus and marine profiles', value: 'citrus-fresh' },
        { label: 'Musk & powder', icon: '−', description: 'Avoid musky, powdery and soapy profiles', value: 'musk' }
      ]
    }
  ];

  window.ParfQuizV5 = {
    PRIVATE_COLLECTION_HANDLE, LUXURY_COLLECTION_HANDLE, PAGE_SIZE, MAX_CATALOG_PAGES, INTENSE_TERMS, NON_PERFUME_TERMS,
    FAMILY_ALIASES, QUESTIONS
  };

  const applyQuizEnhancements = () => {
    const Q = window.ParfQuizV5;
    if (!Q?.CatalogFinder || !Q?.MatchFinder || !Q?.Finder) return false;
    if (Q.__scentStyleMultiSelectApplied) return true;
    Q.__scentStyleMultiSelectApplied = true;

    const familyQuestion = QUESTIONS.find((question) => question.id === 'family');
    const familySelectionLimit = Number(familyQuestion?.maxSelections) || 3;

    const selectedFamilyAnswers = (finder) => (
      Array.isArray(finder.answers.family) ? finder.answers.family : []
    );

    const updateFamilyUi = (finder) => {
      const question = QUESTIONS[finder.currentQuestionIndex];
      if (question?.id !== 'family') return;

      const selected = selectedFamilyAnswers(finder);
      const selectedValues = new Set(selected.map((answer) => answer.value));

      if (finder.elements.options) {
        finder.elements.options.setAttribute('role', 'group');
        finder.elements.options.setAttribute('aria-label', `Choose up to ${familySelectionLimit} scent styles`);

        finder.elements.options.querySelectorAll('.quiz-option').forEach((element, index) => {
          const option = familyQuestion.options[index];
          const isSelected = option ? selectedValues.has(option.value) : false;
          element.classList.toggle('selected', isSelected);
          element.setAttribute('role', 'checkbox');
          element.setAttribute('aria-checked', String(isSelected));
          element.setAttribute(
            'aria-disabled',
            String(!isSelected && selected.length >= familySelectionLimit)
          );
        });
      }

      if (finder.elements.questionHelp) {
        finder.elements.questionHelp.textContent = `${familyQuestion.help} ${selected.length} of ${familySelectionLimit} selected.`;
      }

      if (finder.elements.skip) {
        finder.elements.skip.textContent = selected.length ? 'Continue' : 'Skip this question';
        finder.elements.skip.setAttribute(
          'aria-label',
          selected.length ? `Continue with ${selected.length} selected scent style${selected.length === 1 ? '' : 's'}` : 'Skip this question'
        );
      }
    };

    const originalRenderQuestion = Q.MatchFinder.prototype.renderQuestion;
    Q.MatchFinder.prototype.renderQuestion = function renderQuestionWithFamilyMultiSelect() {
      originalRenderQuestion.call(this);

      const question = QUESTIONS[this.currentQuestionIndex];
      if (question?.id === 'family') {
        if (!Array.isArray(this.answers.family)) {
          this.answers.family = this.answers.family ? [this.answers.family] : [];
        }
        updateFamilyUi(this);
      } else {
        if (this.elements.options) {
          this.elements.options.setAttribute('role', 'radiogroup');
          this.elements.options.setAttribute('aria-label', 'Answer choices');
        }
        if (this.elements.skip) {
          this.elements.skip.textContent = 'Skip this question';
          this.elements.skip.setAttribute('aria-label', 'Skip this question');
        }
      }
    };

    const originalChoose = Q.MatchFinder.prototype.choose;
    Q.MatchFinder.prototype.choose = function chooseWithFamilyMultiSelect(optionIndex) {
      const question = QUESTIONS[this.currentQuestionIndex];
      if (question?.id !== 'family') {
        return originalChoose.call(this, optionIndex);
      }

      const option = question.options[optionIndex];
      if (!option) return;

      const selected = selectedFamilyAnswers(this).slice();
      const existingIndex = selected.findIndex((answer) => answer.value === option.value);

      if (existingIndex >= 0) {
        selected.splice(existingIndex, 1);
      } else {
        if (selected.length >= familySelectionLimit) return;
        selected.push(option);
      }

      this.answers.family = selected;
      updateFamilyUi(this);
    };

    const originalSkip = Q.MatchFinder.prototype.skip;
    Q.MatchFinder.prototype.skip = function skipOrContinueFamily() {
      const question = QUESTIONS[this.currentQuestionIndex];
      if (question?.id === 'family' && selectedFamilyAnswers(this).length) {
        this.advance();
        return;
      }
      originalSkip.call(this);
    };

    const originalAggregatePreferenceWeights = Q.MatchFinder.prototype.aggregatePreferenceWeights;
    Q.MatchFinder.prototype.aggregatePreferenceWeights = function aggregateMultiFamilyWeights() {
      const weights = originalAggregatePreferenceWeights.call(this);
      selectedFamilyAnswers(this).forEach((answer) => {
        Object.entries(answer.weights || {}).forEach(([family, value]) => {
          weights[family] = (weights[family] || 0) + Number(value || 0);
        });
      });
      return weights;
    };

    const originalMatchesAvoidance = Q.MatchFinder.prototype.matchesAvoidance;
    Q.MatchFinder.prototype.matchesAvoidance = function matchExpandedMuskPowderAvoidance(candidate, avoidance) {
      if (avoidance !== 'musk') {
        return originalMatchesAvoidance.call(this, candidate, avoidance);
      }

      const mainScent = String(candidate.mainScent || '').toLowerCase();
      return ['musk', 'powdery'].some((family) => {
        const aliases = FAMILY_ALIASES[family] || [family];
        const isMainFamily = aliases.some((alias) => mainScent.includes(alias));
        return isMainFamily || (candidate.familyScores[family] || 0) >= 2;
      });
    };

    const originalRenderResults = Q.MatchFinder.prototype.renderResults;
    Q.MatchFinder.prototype.renderResults = function renderResultsWithMultiFamilySummary(recommendations) {
      originalRenderResults.call(this, recommendations);

      if (!this.elements.resultsFilters) return;

      const selectedPreferences = ['gender', 'intensity', 'occasion', 'climate', 'mood', 'family', 'avoid']
        .flatMap((questionId) => {
          const answer = this.answers[questionId];
          return Array.isArray(answer) ? answer.map((item) => item?.label) : [answer?.label];
        })
        .filter((label) => label && label !== 'No exclusions' && label !== 'No preference');

      this.elements.resultsFilters.textContent = selectedPreferences.join(' · ') || 'Open to all styles';
    };

    const originalBuildProductCandidates = Q.CatalogFinder.prototype.buildProductCandidates;
    Q.CatalogFinder.prototype.buildProductCandidates = function buildCandidatesWithCollectionMembership(product, isPrivate, isLuxury = false) {
      return originalBuildProductCandidates.call(this, product, isPrivate).map((candidate) => {
        const collections = [];
        if (isPrivate) collections.push('private');
        if (isLuxury) collections.push('luxury');
        if (!collections.length) collections.push('core');

        return {
          ...candidate,
          collection: isPrivate ? 'private' : (isLuxury ? 'luxury' : 'core'),
          collections
        };
      });
    };

    Q.CatalogFinder.prototype.loadCatalog = async function loadCatalogWithCuratedRanges() {
      const safeCollection = async (handle) => {
        try {
          return await this.fetchCollectionProducts(handle);
        } catch (error) {
          console.warn(`[Parffragrance Scent Finder] Optional collection "${handle}" could not be loaded:`, error);
          return [];
        }
      };

      try {
        const [allProducts, privateProducts, luxuryProducts] = await Promise.all([
          this.fetchCollectionProducts('all'),
          safeCollection(PRIVATE_COLLECTION_HANDLE),
          safeCollection(LUXURY_COLLECTION_HANDLE)
        ]);

        const privateHandles = new Set(privateProducts.map((product) => product.handle));
        const luxuryHandles = new Set(luxuryProducts.map((product) => product.handle));
        const merged = new Map();

        [...allProducts, ...privateProducts, ...luxuryProducts].forEach((product) => {
          const existing = merged.get(product.handle);
          merged.set(product.handle, { ...existing, ...product });
        });

        this.catalog = Array.from(merged.values())
          .filter((product) => {
            const isPrivate = privateHandles.has(product.handle);
            const isLuxury = luxuryHandles.has(product.handle);
            return this.isPerfumeProduct(product, isPrivate || isLuxury);
          })
          .flatMap((product) => {
            const isPrivate = privateHandles.has(product.handle);
            const isLuxury = luxuryHandles.has(product.handle);
            return this.buildProductCandidates(product, isPrivate, isLuxury);
          });

        if (!this.catalog.length) {
          throw new Error('No eligible perfume variants were returned by the storefront catalogue.');
        }

        this.catalogStatus = 'ready';
        this.catalogError = null;
        this.setLoadState('ready');
        return this.catalog;
      } catch (error) {
        this.catalogStatus = 'error';
        this.catalogError = error;
        this.catalogPromise = null;
        console.error('[Parffragrance Scent Finder] Catalogue loading failed:', error);
        this.setLoadState('error');
        throw error;
      }
    };

    const originalScoreCandidate = Q.MatchFinder.prototype.scoreCandidate;
    Q.MatchFinder.prototype.scoreCandidate = function scoreCandidateWithRangeReasons(candidate) {
      const scored = originalScoreCandidate.call(this, candidate);
      const reasons = [...(scored.matchReasons || [])];

      if (candidate.collections?.includes('private')) reasons.push('Private Collection');
      if (candidate.collections?.includes('luxury')) reasons.push('Luxury Collection');

      return {
        ...scored,
        matchReasons: Array.from(new Set(reasons)).slice(0, 4)
      };
    };

    const originalPrettyFamily = Q.Finder.prototype.prettyFamily;
    Q.Finder.prototype.prettyFamily = function prettyExpandedFamily(family) {
      if (family === 'fruity') return 'Fruity';
      if (family === 'powdery') return 'Powdery';
      return originalPrettyFamily.call(this, family);
    };

    const originalRenderProductCard = Q.Finder.prototype.renderProductCard;
    Q.Finder.prototype.renderProductCard = function renderProductCardWithLuxuryBadge(product, index) {
      let html = originalRenderProductCard.call(this, product, index);

      if (product.collections?.includes('luxury')) {
        html = html.replace(
          '</div><h3 class="quiz-product__title">',
          '<span class="quiz-range-badge quiz-range-badge--luxury">Luxury</span></div><h3 class="quiz-product__title">'
        );
      }

      return html;
    };

    return true;
  };

  const scheduleEnhancements = () => {
    const tryApply = (attempt = 0) => {
      if (applyQuizEnhancements()) return;
      if (attempt < 40) window.setTimeout(() => tryApply(attempt + 1), 50);
    };
    tryApply();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleEnhancements, { once: true });
  } else {
    scheduleEnhancements();
  }
})();
